// Global Instructions Rule Applied!
//
// Shape of POST /deals/pipeline-budget (view__deal_pipeline_budget): the
// Deal Pipeline → Budget tab. One entry per followed fund with its capital
// (committed / called / uncalled), cash (cash balance / payables / net cash),
// open deal commitments and the two Remaining figures; one row per
// (deal, fund) open commitment; the totals across the funds.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

const figures = {
  prog__committed: z.number().nullish(),
  prog__called: z.number().nullish(),
  prog__uncalled: z.number().nullish(),
  prog__cash: z.number().nullish(),
  prog__payables: z.number().nullish(),
  prog__net_cash: z.number().nullish(),
  prog__commitments: z.number().nullish(),
  prog__cash_remaining: z.number().nullish(),
  prog__capital_remaining: z.number().nullish()
}

export const DealPipelineBudgetFundSchema = z
  .object({
    funds__id: z.string(),
    funds__name: z.string().nullish(),
    currencies__symbol: z.string().nullish(),
    // called including the lines the fund's terms leave out of the commitment
    prog__called_total: z.number().nullish(),
    prog__pct_drawn: z.number().nullish(),
    ...figures
  })
  .passthrough()

export const DealPipelineBudgetTotalsSchema = z.object(figures).passthrough()

export const DealPipelineBudgetCommitmentSchema = z
  .object({
    deals__id: z.string(),
    deals__name: z.string().nullish(),
    funds__id: z.string(),
    pipeline_stages__name: z.string().nullish(),
    deals__pipeline_stage_date: z.string().nullish(),
    deal_commitments__amount: z.number().nullish()
  })
  .passthrough()

export const DealPipelineBudgetSchema = z
  .object({
    as_of: z.string().nullish(),
    funds: z.array(DealPipelineBudgetFundSchema),
    commitments: z.array(DealPipelineBudgetCommitmentSchema),
    totals: DealPipelineBudgetTotalsSchema
  })
  .passthrough()

export type DealPipelineBudgetFund = z.infer<typeof DealPipelineBudgetFundSchema>
export type DealPipelineBudgetTotals = z.infer<typeof DealPipelineBudgetTotalsSchema>
export type DealPipelineBudgetCommitment = z.infer<typeof DealPipelineBudgetCommitmentSchema>
export type DealPipelineBudget = z.infer<typeof DealPipelineBudgetSchema>

/** The figure keys a fund entry and the totals share, in statement order. */
export type DealPipelineBudgetFigureKey = keyof typeof figures

export const validateDealPipelineBudget = (data: unknown): DealPipelineBudget =>
  validateWithZodSchema(DealPipelineBudgetSchema, data, 'deal pipeline budget')
